import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import Modal from "@/components/Modal";
import { BookOpen, Plus, Upload, Download, Trash2, Users, AlertTriangle, CheckCircle2, X, Send } from "lucide-react";

const CATEGORIES = [
  { v: "health_safety",    label: "Health & Safety" },
  { v: "safeguarding",     label: "Safeguarding" },
  { v: "data_protection",  label: "Data Protection" },
  { v: "code_of_conduct",  label: "Code of Conduct" },
  { v: "attendance",       label: "Attendance" },
  { v: "other",            label: "Other" },
];

export default function PoliciesPage() {
  const [rows, setRows] = useState([]);
  const [dash, setDash] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [assignPolicy, setAssignPolicy] = useState(null);
  const apiBase = process.env.REACT_APP_BACKEND_URL;

  const load = async () => {
    setLoading(true);
    try {
      const [list, summary] = await Promise.all([
        api.get("/policies"),
        api.get("/policies/dashboard/summary"),
      ]);
      setRows(list.data || []);
      setDash(summary.data);
    } catch (e) { toast.error("Failed to load policies"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (p) => {
    if (!window.confirm(`Delete policy "${p.name}"?`)) return;
    await api.delete(`/policies/${p.id}`);
    toast.success("Policy archived");
    load();
  };

  return (
    <div className="fade-up">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="label-eyebrow mb-2">People</div>
          <h1 className="font-display text-4xl font-bold flex items-center gap-3"><BookOpen className="w-8 h-8" /> Policies</h1>
          <p className="text-[var(--cc-text-soft)] mt-1">Create company policies, assign them to employees, and track acknowledgments.</p>
        </div>
        <button className="btn-primary inline-flex items-center gap-1.5" onClick={() => setShowCreate(true)} data-testid="new-policy-button">
          <Plus className="w-4 h-4" /> New policy
        </button>
      </div>

      {dash && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <DashCard icon={BookOpen} label="Total policies" value={dash.total_policies} testId="dash-total" />
          <DashCard icon={Users} label="Pending acks" value={dash.pending_acknowledgments} testId="dash-pending" />
          <DashCard icon={AlertTriangle} label="Overdue acks" value={dash.overdue_acknowledgments} accent={dash.overdue_acknowledgments > 0} testId="dash-overdue" />
          <DashCard icon={CheckCircle2} label="Due for review" value={dash.due_for_review?.length || 0} testId="dash-review" />
        </div>
      )}

      <div className="cc-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[var(--cc-surface)]">
            <tr className="text-left">
              <th className="px-5 py-3 label-eyebrow">Policy</th>
              <th className="px-5 py-3 label-eyebrow">Category</th>
              <th className="px-5 py-3 label-eyebrow">Version</th>
              <th className="px-5 py-3 label-eyebrow">Effective</th>
              <th className="px-5 py-3 label-eyebrow">Ack / Pending</th>
              <th className="px-5 py-3 label-eyebrow text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="6" className="text-center py-10 text-[var(--cc-text-soft)]">Loading…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan="6" className="text-center py-10 text-[var(--cc-text-soft)]" data-testid="empty-policies">No policies yet — click <strong>New policy</strong> to create the first one.</td></tr>}
            {rows.map((p) => {
              const dashRow = dash?.per_policy?.find((r) => r.id === p.id);
              return (
                <tr key={p.id} className="border-t border-[var(--cc-border)]" data-testid={`policy-row-${p.id}`}>
                  <td className="px-5 py-3">
                    <div className="font-medium">{p.name}</div>
                    {p.description && <div className="text-[11px] text-[var(--cc-text-soft)] mt-0.5 truncate max-w-md">{p.description}</div>}
                  </td>
                  <td className="px-5 py-3 text-xs"><span className="pill pill-info text-[10px]">{p.category?.replace("_", " ")}</span></td>
                  <td className="px-5 py-3 text-xs">{p.version}</td>
                  <td className="px-5 py-3 text-xs text-[var(--cc-text-soft)]">{p.effective_date?.slice(0, 10)}</td>
                  <td className="px-5 py-3 text-xs">
                    <span className="text-[var(--cc-sage)] font-bold">{dashRow?.acknowledged ?? 0}</span>
                    {" / "}
                    <span className="text-[var(--cc-accent)] font-bold">{dashRow?.pending ?? 0}</span>
                  </td>
                  <td className="px-5 py-3 text-right">
                    <button className="btn-ghost p-1.5" title="Assign" onClick={() => setAssignPolicy(p)} data-testid={`assign-policy-${p.id}`}><Send className="w-4 h-4" /></button>
                    <a href={`${apiBase}/api/policies/${p.id}/download`} target="_blank" rel="noreferrer" className="btn-ghost p-1.5" title="Download"><Download className="w-4 h-4" /></a>
                    <button className="btn-ghost p-1.5" title="Archive" onClick={() => remove(p)} data-testid={`delete-policy-${p.id}`}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showCreate && <CreatePolicyModal onClose={() => setShowCreate(false)} onSaved={() => { setShowCreate(false); load(); }} />}
      {assignPolicy && <AssignModal policy={assignPolicy} onClose={() => setAssignPolicy(null)} onSaved={() => { setAssignPolicy(null); load(); }} />}
    </div>
  );
}

function DashCard({ icon: Icon, label, value, accent, testId }) {
  return (
    <div className="cc-card p-4" data-testid={testId}>
      <Icon className={`w-5 h-5 mb-2 ${accent ? "text-[var(--cc-accent)]" : "text-[var(--cc-primary)]"}`} />
      <div className="label-eyebrow mb-1">{label}</div>
      <div className="font-display text-2xl font-bold">{value}</div>
    </div>
  );
}

function CreatePolicyModal({ onClose, onSaved }) {
  const [name, setName] = useState("");
  const [category, setCategory] = useState("health_safety");
  const [description, setDescription] = useState("");
  const [effective, setEffective] = useState(new Date().toISOString().slice(0, 10));
  const [review, setReview] = useState("");
  const [assignAll, setAssignAll] = useState(true);
  const [dueDays, setDueDays] = useState(7);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!name.trim()) return toast.error("Policy name required");
    if (!file) return toast.error("Policy document required");
    if (file.size > 20 * 1024 * 1024) return toast.error("File exceeds 20 MB");
    setBusy(true);
    try {
      const form = new FormData();
      form.append("name", name.trim());
      form.append("category", category);
      form.append("description", description);
      form.append("effective_date", effective);
      if (review) form.append("review_date", review);
      form.append("assign_to_all", assignAll ? "true" : "false");
      form.append("due_in_days", String(dueDays));
      form.append("file", file);
      const { data } = await api.post("/policies", form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Policy created${data.assigned_count ? ` and assigned to ${data.assigned_count} employees` : ""}`);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create policy");
    } finally { setBusy(false); }
  };

  return (
    <Modal onClose={onClose} widthClass="max-w-xl" testId="create-policy-modal">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-xl font-bold flex items-center gap-2"><Plus className="w-5 h-5" /> Create new policy</h2>
        <button className="btn-ghost" onClick={onClose}><X className="w-4 h-4" /></button>
      </div>
      <div className="space-y-3">
        <div>
          <label className="label-eyebrow block mb-1">Policy name *</label>
          <input className="cc-input" value={name} onChange={(e) => setName(e.target.value)} data-testid="policy-name-input" />
        </div>
        <div>
          <label className="label-eyebrow block mb-1">Category *</label>
          <select className="cc-select" value={category} onChange={(e) => setCategory(e.target.value)} data-testid="policy-category-select">
            {CATEGORIES.map((c) => <option key={c.v} value={c.v}>{c.label}</option>)}
          </select>
        </div>
        <div>
          <label className="label-eyebrow block mb-1">Description</label>
          <textarea className="cc-input" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label-eyebrow block mb-1">Effective date *</label>
            <input type="date" className="cc-input" value={effective} onChange={(e) => setEffective(e.target.value)} data-testid="policy-effective-input" />
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Review date</label>
            <input type="date" className="cc-input" value={review} onChange={(e) => setReview(e.target.value)} />
          </div>
        </div>
        <div>
          <label className="label-eyebrow block mb-1">Policy document (PDF) *</label>
          <input type="file" accept=".pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} className="block w-full text-sm" data-testid="policy-file-input" />
          {file && <div className="text-[11px] text-[var(--cc-text-soft)] mt-1">{file.name} · {(file.size / 1024).toFixed(1)} KB</div>}
        </div>
        <div className="flex items-center gap-3 pt-2 border-t border-[var(--cc-border)]">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={assignAll} onChange={(e) => setAssignAll(e.target.checked)} data-testid="policy-assign-all" /> Assign to all active employees
          </label>
          {assignAll && (
            <>
              <span className="text-xs text-[var(--cc-text-soft)]">Due in</span>
              <input type="number" min="1" max="60" value={dueDays} onChange={(e) => setDueDays(Number(e.target.value))} className="cc-input w-16 text-sm" data-testid="policy-due-days" />
              <span className="text-xs text-[var(--cc-text-soft)]">days</span>
            </>
          )}
        </div>
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <button className="btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn-primary" onClick={submit} disabled={busy} data-testid="create-policy-submit">{busy ? "Creating…" : "Create policy"}</button>
      </div>
    </Modal>
  );
}

function AssignModal({ policy, onClose, onSaved }) {
  const [employees, setEmployees] = useState([]);
  const [selected, setSelected] = useState({});
  const [dueDays, setDueDays] = useState(7);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/employees").then(({ data }) => setEmployees(data || []));
  }, []);

  const submit = async (assignAll) => {
    setBusy(true);
    try {
      const payload = assignAll
        ? { assign_to_all: true, due_in_days: dueDays }
        : { employee_ids: Object.keys(selected).filter((k) => selected[k]), due_in_days: dueDays };
      if (!assignAll && payload.employee_ids.length === 0) {
        setBusy(false);
        return toast.error("Select at least one employee");
      }
      const { data } = await api.post(`/policies/${policy.id}/assign`, payload);
      toast.success(`Assigned to ${data.assigned} employees (${data.skipped} skipped)`);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Assign failed");
    } finally { setBusy(false); }
  };

  return (
    <Modal onClose={onClose} widthClass="max-w-lg" testId="assign-policy-modal">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-xl font-bold">Assign "{policy.name}"</h2>
        <button className="btn-ghost" onClick={onClose}><X className="w-4 h-4" /></button>
      </div>
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <span className="text-xs">Due in</span>
          <input type="number" min="1" max="60" value={dueDays} onChange={(e) => setDueDays(Number(e.target.value))} className="cc-input w-20 text-sm" />
          <span className="text-xs">days</span>
        </div>
        <div className="max-h-80 overflow-y-auto border border-[var(--cc-border)] rounded p-2">
          {employees.map((e) => (
            <label key={e.id} className="flex items-center gap-2 p-1.5 hover:bg-[var(--cc-surface)] rounded cursor-pointer" data-testid={`assign-emp-${e.id}`}>
              <input type="checkbox" checked={!!selected[e.id]} onChange={(ev) => setSelected({ ...selected, [e.id]: ev.target.checked })} />
              <span className="text-sm">{e.full_name} <span className="text-[var(--cc-text-soft)] text-xs">— {e.job_title}</span></span>
            </label>
          ))}
          {employees.length === 0 && <div className="text-center text-[var(--cc-text-soft)] text-sm py-4">No employees yet.</div>}
        </div>
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <button className="btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn-secondary" onClick={() => submit(true)} disabled={busy} data-testid="assign-all-button">Assign to ALL</button>
        <button className="btn-primary" onClick={() => submit(false)} disabled={busy} data-testid="assign-selected-button">Assign selected</button>
      </div>
    </Modal>
  );
}
